import { Component } from 'react';
import { connect } from 'react-redux';
import Select from 'react-select';
import { ToastManagerProp, withToastManager } from 'react-toast-notifications';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import cn from 'classnames';
import DatePicker from 'components/DatePicker';
import Dialog from 'components/Dialog';
import scss from 'components/shared.module.scss';
import * as dt from 'core/datetime';
import { apiErrorHandler } from 'core/errors';
import { DATE_FORMAT } from 'globals/constants';
import { Dispatch } from 'redux';
import { DELETE_WEEKLY_CLASS, UPDATE_COURSE_DATES, UPDATE_COURSE_TIMELINE } from 'redux/actions';
import { API, request } from 'redux/api';
import { RootState } from 'redux/reducers';

import { ReactComponent as EditIcon } from 'assets/edit.svg';
import ErrorIcon from 'assets/error.png';
import { ReactComponent as WarningIcon } from 'assets/warning-orange.svg';

import { Button } from '../UtilElements';
import WeeklyClass from '../WeeklyClass';
import CreateWeeklyClassDialog from './CreateWeeklyClassDialog';

interface SaveCourseSchedulePayload {
  courseId: string;
  dates: {
    startDate: string; // YYYY-MM-DD
    endDate: string; // YYYY-MM-DD
    courseTimezone: string;
    schedule: CourseScheduleData[];
  };
}

interface SaveCourseScheduleResponse {
  message: string;
  classes: CourseTimelineClass[];
}

const mapState = (state: RootState) => ({
  course: state.courseInCreation,
});

const mapDispatch = (dispatch: Dispatch) => ({
  updateCourseDates: (dates: Partial<CourseDetails['dates']>) => {
    dispatch({
      type: UPDATE_COURSE_DATES,
      payload: dates,
    });
  },
  updateTimeline: (data: CourseTimelineClass[]) => {
    dispatch({
      type: UPDATE_COURSE_TIMELINE,
      payload: data,
    });
  },
  deleteWeeklyClass: (key: string) => {
    dispatch({
      type: DELETE_WEEKLY_CLASS,
      payload: key,
    });
  },
});

interface OwnProps {
  onPrev(): void;
  onNext(): void;
}

type Props = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch> & ToastManagerProp & OwnProps;

interface State {
  isWeeklyAddClassVisible: boolean;
  isSavingSchedule: boolean;
  setupClassesManuallyWarning: boolean;
  timezones: string[];
  selectedTimezone: string | null;
  editingTimezone: boolean;
  timezoneError: boolean;
  startDate: Date | undefined;
  startDateError: boolean;
  endDate: Date | undefined;
  endDateError: boolean;
}

class CourseSchedule extends Component<Props, State> {
  state: State = {
    isWeeklyAddClassVisible: false,
    isSavingSchedule: false,
    setupClassesManuallyWarning: false,
    timezones: [],
    selectedTimezone: null,
    editingTimezone: false,
    timezoneError: false,
    startDate: undefined,
    startDateError: false,
    endDate: undefined,
    endDateError: false,
  };

  async componentDidMount() {
    const dates = this.props.course?.dates;
    if (!dates) return;
    this.setState({
      timezones: dates.courseTimezone.available,
      selectedTimezone: dates.courseTimezone.valid ? dates.courseTimezone.value : null,
      timezoneError: !dates.courseTimezone.valid,
      startDate: dates.startDate > 0 ? dt.fromUnix(dates.startDate) : undefined,
      startDateError: dates.startDate <= 0,
      endDate: dates.endDate > 0 ? dt.fromUnix(dates.endDate) : undefined,
      endDateError: dates.endDate <= 0,
    });
  }

  openWeeklyAddClassDialog = () => {
    // disableScroll ? disableScroll() : null;
    this.setState({ isWeeklyAddClassVisible: true });
  };

  closeWeeklyAddClassDialog = () => {
    this.setState({
      isWeeklyAddClassVisible: false,
    });
  };

  addClasses = (schedule: ClassSchedule) => {
    if (!this.props.course) return;

    const oldSchedule = this.props.course.dates.schedule;
    const newSchedule: CourseScheduleData[] = schedule.repeatsOn.map((d) => {
      return {
        type: schedule.classType,
        day: d,
        startTime: dt.serialize24HoursTime(schedule.from, 24),
        endTime: dt.serialize24HoursTime(schedule.to, 24),
        venue: schedule.venue,
        isOnlineMeeting: schedule.isOnlineMeeting,
      };
    });

    const sortedSchedule = [...oldSchedule, ...newSchedule].sort((a, b) => {
      const s1 = `${a.day},${a.startTime},${a.endTime}`;
      const s2 = `${b.day},${b.startTime},${b.endTime}`;
      if (s1 < s2) return -1;
      if (s1 > s2) return 1;
      return 0;
    });

    this.props.updateCourseDates({ schedule: sortedSchedule });

    this.setState({
      isWeeklyAddClassVisible: false,
    });
  };

  deleteWeeklyClass = (key: string) => {
    this.props.deleteWeeklyClass(key);
  };

  closeSetUpClassesManuallyWarning = () => {
    this.setState({ setupClassesManuallyWarning: false });
  };

  saveSchedule = async (data: SaveCourseSchedulePayload) => {
    const { course } = this.props;

    if (!course || !this.state.endDate || !this.state.selectedTimezone) return;

    try {
      const { classes } = await request<SaveCourseScheduleResponse>({
        data,
        method: 'patch',
        url: API.COURSE_CREATE_SAVE_SCHEDULE,
      });

      this.setState({ isSavingSchedule: false });

      this.props.updateCourseDates({
        endDate: dt.toUnix(this.state.endDate),
        courseTimezone: {
          ...course.dates.courseTimezone,
          valid: 1,
          value: this.state.selectedTimezone,
        },
      });

      this.props.updateTimeline(classes);
      this.props.onNext();
    } catch (e: any) {
      apiErrorHandler(this.props.toastManager, e, () => {
        this.setState({ isSavingSchedule: false });
      });
    }
  };

  proceedToImport = async () => {
    const { course } = this.props;

    if (!course || !this.state.endDate || !this.state.selectedTimezone) return;

    this.setState({
      isSavingSchedule: true,
      setupClassesManuallyWarning: false,
    });

    const data: SaveCourseSchedulePayload = {
      courseId: course.courseId,
      dates: {
        startDate: dt.format(course.dates.startDate, 'yyyy-MM-dd'),
        endDate: dt.format(this.state.endDate, 'yyyy-MM-dd'),
        courseTimezone: this.state.selectedTimezone,
        schedule: [],
      },
    };

    await this.saveSchedule(data);
  };

  handleGoBack = async () => {
    this.props.onPrev();
  };

  handleSaveAndContinue = async () => {
    const { course } = this.props;
    const { startDate, endDate, selectedTimezone } = this.state;

    if (!course) return;

    if (!startDate || !endDate || !selectedTimezone) {
      return this.setState({
        startDateError: !startDate,
        endDateError: !endDate,
        timezoneError: !selectedTimezone,
      });
    }

    if (!course.dates.schedule.length) {
      return this.setState({ setupClassesManuallyWarning: true });
    }

    this.setState({
      startDateError: false,
      endDateError: false,
      timezoneError: false,
      isSavingSchedule: true,
    });

    const data: SaveCourseSchedulePayload = {
      courseId: course.courseId,
      dates: {
        startDate: dt.format(startDate, 'yyyy-MM-dd'),
        endDate: dt.format(endDate, 'yyyy-MM-dd'),
        courseTimezone: selectedTimezone,
        schedule: course.dates.schedule,
      },
    };

    await this.saveSchedule(data);
  };

  render() {
    if (!this.props.course) return null;
    const course = this.props.course;

    const tomorrowUnix = dt.toUnix(dt.add(new Date(), 1, 'day'));
    const nextDayOfStartDateUnix = dt.toUnix(dt.add(this.state.startDate || dt.now(), 1, 'day'));
    const minEndDate = dt.fromUnix(Math.max(tomorrowUnix, nextDayOfStartDateUnix));

    let duration: number | string = '--';

    if (this.state.startDate && this.state.endDate) {
      duration = dt.differenceInWeeks(this.state.endDate, this.state.startDate) + 1;
    }

    return (
      <>
        <form className='import-course__form with-actions'>
          <div className='import-course__form-content'>
            <label className='import-course__label'>Dates</label>
            <div className='import-course__cell'>
              <span> Course Starts on</span>
              <DatePicker
                className={cn(scss['edit-icon'], {
                  bold: !!this.state.startDate,
                  'form-field--empty': !this.state.startDate,
                })}
                minDate={dt.now()}
                maxDate={dt.fromUnix(course.dateLimit)}
                value={this.state.startDate}
                onChange={(date) =>
                  this.setState({
                    startDate: date,
                    startDateError: false,
                    endDate: undefined,
                    endDateError: true,
                  })
                }
              >
                {this.state.startDate ? dt.format(this.state.startDate, DATE_FORMAT) : 'Select date'}
                <EditIcon />
              </DatePicker>
            </div>
            {this.state.startDateError && (
              <div className='import-course__error'>
                <img src={ErrorIcon} alt='' />
                <span>Please select course start date</span>
              </div>
            )}
            <div className='import-course__cell'>
              <span> Ends on</span>
              <DatePicker
                className={cn(scss['edit-icon'], {
                  bold: !!this.state.endDate,
                  'form-field--empty': !this.state.endDate,
                })}
                minDate={minEndDate}
                maxDate={dt.fromUnix(course.dateLimit)}
                value={this.state.endDate}
                onChange={(date) => this.setState({ endDate: date, endDateError: false })}
              >
                {this.state.endDate ? dt.format(this.state.endDate, DATE_FORMAT) : 'Select date'}
                <span></span>
                <EditIcon />
              </DatePicker>
            </div>
            {this.state.endDateError && (
              <div className='import-course__error'>
                <img src={ErrorIcon} alt='' />
                <span>Please select course end date</span>
              </div>
            )}
            <div className='import-course__cell'>
              <span>Duration</span>
              <span className='bold'>{duration} weeks</span>
            </div>
            <div className='import-course__cell'>
              <span>TimeZone</span>
              {course.dates.courseTimezone.valid ? (
                <span className='bold'>{course.dates.courseTimezone.value}</span>
              ) : (
                <span
                  className={cn(scss['edit-icon'], {
                    bold: !!this.state.selectedTimezone,
                    'form-field--empty': !this.state.selectedTimezone,
                  })}
                  onClick={() => this.setState({ editingTimezone: true })}
                >
                  {this.state.selectedTimezone || 'Select timezone'}
                  <EditIcon />
                </span>
              )}
            </div>
            {this.state.timezoneError && (
              <div className='import-course__error'>
                <img src={ErrorIcon} alt='' />
                <span>Please select timezone</span>
              </div>
            )}
            <label className='import-course__label'>Weekly Class Schedule</label>
            {course.dates.schedule.length === 0 ? (
              <div className='import-course__gutters'>No weekly classes scheduled yet</div>
            ) : (
              <TransitionGroup className='import-course__schedule'>
                {course.dates.schedule.map((sc) => (
                  <CSSTransition
                    key={sc.day + sc.startTime + sc.endTime}
                    classNames='removeFromList'
                    timeout={{ enter: 60, exit: 300 }}
                  >
                    <WeeklyClass
                      {...sc}
                      className='import-course__gutters'
                      onDelete={this.deleteWeeklyClass.bind(this, sc.day + sc.startTime + sc.endTime)}
                    />
                  </CSSTransition>
                ))}
              </TransitionGroup>
            )}
          </div>
          <div className='import-course__form-actions'>
            <Button
              variant='contained'
              color='info'
              size='large'
              fullWidth
              onClick={this.openWeeklyAddClassDialog}
            >
              Add a new weekly class
            </Button>
          </div>
          {this.modals()}
        </form>
        <section className='import-course__info'>
          <h2 className='import-course__title'>Step 2: Course Schedule</h2>
          <p className='import-course__description'>
            Here, we take the course start and end dates and ask you to specify an optional weekly schedule to
            automatically populate your course timeline on Acadly. In case you choose not to provide a weekly
            schedule, you will have to create all the class meetings manually, which can be a little effort
            intensive. We strongly recommend that you provide a weekly class schedule, if the course follows
            one.
          </p>
          <div className='import-course__actions'>
            <div className='import-course__action'>
              <Button
                preventMinWidth
                className='import-course__action__back'
                variant='text'
                color='info'
                size='large'
                onClick={() => this.handleGoBack()}
              >
                Back
              </Button>
            </div>
            <div className='import-course__action'>
              <Button
                variant='contained'
                color='success'
                size='large'
                disabled={this.state.isSavingSchedule || this.state.endDateError || this.state.timezoneError}
                onClick={() => this.handleSaveAndContinue()}
                isLoading={this.state.isSavingSchedule}
              >
                Save and Continue
              </Button>
              <p className='note note__error'>
                {this.state.startDateError
                  ? 'Please select course start date'
                  : this.state.endDateError
                  ? 'Please select course end date'
                  : this.state.timezoneError
                  ? 'Please select course timezone'
                  : ''}
              </p>
            </div>
          </div>
        </section>
      </>
    );
  }

  private modals() {
    const course = this.props.course!;

    return (
      <>
        <CreateWeeklyClassDialog
          isOpen={this.state.isWeeklyAddClassVisible}
          classeSchedules={course.dates.schedule}
          onCancel={this.closeWeeklyAddClassDialog}
          onDone={this.addClasses}
        />
        <Dialog
          isOpen={this.state.setupClassesManuallyWarning}
          variant='small'
          title='Importing Course Schedule'
          footer={
            <>
              <Button
                variant='text'
                color='error'
                size='small'
                onClick={this.closeSetUpClassesManuallyWarning}
              >
                NO
              </Button>
              <Button variant='text' color='success' size='small' onClick={this.proceedToImport}>
                YES
              </Button>
            </>
          }
        >
          <p className={`note note__warning note__no-h-space ${scss['warning-line']}`}>
            <WarningIcon className={scss['inline-warning-icon']} />
            <span>
              You have not specified a weekly schedule. You will have to add classes manually to the timeline.
            </span>
          </p>
          <p className='bold note note__no-h-space'>Are you sure you want to save and continue?</p>
        </Dialog>
        <Dialog
          isOpen={this.state.editingTimezone}
          variant='small'
          title='Select Timezone'
          footer={
            <Button
              variant='text'
              color='success'
              size='small'
              onClick={() => this.setState({ editingTimezone: false })}
              preventHoverStyles
            >
              Close
            </Button>
          }
        >
          {/**
           * FIXME: fade-in animation is broken because of <Select />
           * @see https://github.com/JedWatson/react-select/issues/165
           */}
          {(modalTargetEl) => (
            <Select
              classNamePrefix='react-select'
              placeholder='Select timezone'
              onChange={(tz) =>
                this.setState({
                  selectedTimezone: (tz as any)?.value,
                  timezoneError: false,
                  editingTimezone: false,
                })
              }
              options={this.state.timezones.map((tz) => ({ label: tz, value: tz }))}
              menuPortalTarget={modalTargetEl}
              value={
                this.state.selectedTimezone
                  ? { label: this.state.selectedTimezone, value: this.state.selectedTimezone }
                  : undefined
              }
            />
          )}
        </Dialog>
      </>
    );
  }
}

export default connect(mapState, mapDispatch)(withToastManager(CourseSchedule));
